import { Icon } from "@iec-id/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { NAV_SECTIONS, NOTIFICATIONS_SECTION } from "./nav-config";

/**
 * Ссылка «Назад» под мобильной шапкой.
 *
 * На телефоне левого меню нет, а ящик открывается двумя нажатиями — вернуться
 * на уровень выше было нечем, кроме системной кнопки браузера. Это ССЫЛКА, а
 * не `history.back()`: адрес виден заранее, средний клик и «открыть в новой
 * вкладке» работают, а поведение не зависит от того, как человек попал на
 * раздел (по ссылке из письма история пуста, и `back` увёл бы его из витрины).
 *
 * Уровень выше считается по адресу: `/points/spend` → `/points`, а любой
 * раздел первого уровня → Главная. На самой Главной ссылки нет — возвращаться
 * некуда.
 */
export function BackLink() {
	const { t } = useTranslation("common");
	const pathname = useRouterState({ select: (state) => state.location.pathname });

	const target = parentPath(pathname);
	if (!target) return null;

	return (
		<Link
			to={target.to}
			// Видимая подпись — просто «Назад»: название раздела в ней ничего не
			// добавляет (человек и так только что оттуда) и вдобавок не
			// склоняется — выходило «Назад в Главная». Доступное имя при этом
			// остаётся полным: скринридер зачитывает ссылку вне окружения, и
			// голое «Назад» в списке ссылок страницы неотличимо от любого
			// другого.
			aria-label={t("nav.backTo", { section: t(target.labelKey) })}
			className="iec-press-glyph flex w-fit items-center gap-1.5 py-1 text-fg-muted no-underline hover:text-fg lg:hidden"
		>
			<Icon name="ArrowLeft" size={20} />
			<span className="t-meta">{t("nav.back")}</span>
		</Link>
	);
}

/** Куда ведёт «назад» с этого адреса. `null` — уже на Главной. */
function parentPath(pathname: string): { to: string; labelKey: string } | null {
	const clean = pathname.replace(/\/+$/, "");
	if (clean === "" || clean === "/") return null;

	const sections = [...NAV_SECTIONS, NOTIFICATIONS_SECTION];
	const segments = clean.split("/").filter(Boolean);

	// Вложенный экран (`/points/spend`) — возврат в свой раздел, а не на Главную.
	if (segments.length > 1) {
		const parent = `/${segments.slice(0, -1).join("/")}`;
		const section = sections.find((item) => item.to === parent);
		if (section) return { to: section.to, labelKey: section.labelKey };
	}

	return { to: "/", labelKey: "nav.overview" };
}
